import React, { Component } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { Breakpoint, ButtonTheme, FirstPage, LayoutGap, SelectionStyle, SelectionType } from '@philips-internal/oswald-ui-constants';
import { useEditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ClickEvent, PageEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { SelectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types/selectable-list.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isContentfulString, isEmptyArrayOrObject, isEnumValid, isFunction, isMobileResolution, propagateProps } from '@philips-internal/oswald-ui-utils';
import { useIsMobileResolution } from '../../hooks/use-is-mobile-resolution.js';
import { Button } from '../button/button.js';
import { CardProperties } from '../card/card-types.js';
import { FetchContext } from '../fetch/fetch-context.js';
import { Loader } from '../loader/loader.js';
import { NotFound } from '../not-found/not-found.js';
import { BaseCardListColumnsByBreakpoint, BaseCardListOrientation } from './base-card-list-constants.js';
import './base-card-list.css';
function Loading(props) {
    const editorContext = useEditorContext();
    const isMobile = useIsMobileResolution();
    if (!props.loading || editorContext?.isDesigning()) {
        return null;
    }
    return (React.createElement(Loader, { centered: true, className: "c-base-card-list__loader u-hide@empty", size: isMobile ? 32 : 64 }));
}
function LoadMore(props) {
    const editorContext = useEditorContext();
    if (!props.loading && !props.done && props.data?.length && !editorContext?.isDesigning()) {
        return (React.createElement(Button, { className: "c-base-card-list__load-more", onClick: props.onClick, theme: ButtonTheme.SECONDARY, value: props.loadMoreExpression }));
    }
    return null;
}
function NotFoundResult(props) {
    const editorContext = useEditorContext();
    if (editorContext?.isDesigning() || !isEmptyArrayOrObject(props.data) || props.loading) {
        return null;
    }
    return (React.createElement(NotFound, { className: "c-base-card-list__not-found", subtitleExpression: props.emptyResultSubtitleExpression, titleExpression: props.emptyResultTitleExpression }));
}
class BaseCardList extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            ...this.state,
            selectedCards: [],
        };
        this.onLoadMoreClick = () => {
            const { limit, onNextPage, page } = this.props;
            if (isFunction(onNextPage)) {
                onNextPage(new PageEvent({ accumulate: true, limit, page: page + 1 }));
            }
        };
        this.onItemClick = (item) => {
            const { onClick } = this.props;
            if (isFunction(onClick)) {
                onClick(new ClickEvent({ value: item }));
            }
        };
        this.onDoubleClick = () => {
            const { onDoubleClick } = this.props;
            if (isFunction(onDoubleClick)) {
                onDoubleClick();
            }
        };
        this.onContextMenuClick = () => {
            const { onContextMenuClick } = this.props;
            if (isFunction(onContextMenuClick)) {
                onContextMenuClick();
            }
        };
        this.onCardSelect = ({ item: card }) => {
            const { onSelectItem } = this.props;
            const items = this.modifyCardSelection(card, false);
            this.setState({ selectedCards: items }, () => {
                if (isFunction(onSelectItem)) {
                    onSelectItem(new SelectItemEvent({ item: card }));
                }
            });
        };
        this.onCardUnselect = ({ item: card }) => {
            const { onUnselectItem } = this.props;
            const { selectedCards } = this.state;
            const items = this.modifyCardSelection(card, true);
            if (items.length !== selectedCards.length) {
                this.setState({
                    selectedCards: items,
                }, () => {
                    if (isFunction(onUnselectItem)) {
                        onUnselectItem(new SelectItemEvent({ item: card }));
                    }
                });
            }
        };
        this.updateCardSelection = (event, card) => {
            const eventFunction = event.selected ? this.onCardSelect : this.onCardUnselect;
            this.onBeforeUpdateCardSelection(event, card);
            eventFunction(new SelectItemEvent({ item: card }));
        };
        this.getMasonryColumnsCountBreakPoints = (cardWidth) => {
            const breakpoints = enumValues(Breakpoint);
            const masonryBreakpoints = {};
            /**
             * Even if these measures are not the real ones compatible with each breakpoint,
             * we need to inform them that way so that masonry knows up to what value
             * the number of columns of each resolution must be respected
             *
             * The following configuration will result in:
             *
             * XSM goes from 1 to 767
             * SM goes from 768 to 1279
             * MD goes from 1279 to any greater value
             *
             * As greater values than MD breakpoint doesn't have column number variation, LG is not needed to be declared
             */
            const breakpointsNamesToSizes = {
                [Breakpoint.MD]: 1279,
                [Breakpoint.SM]: 767,
                [Breakpoint.XSM]: 1,
            };
            for (const breakpoint of breakpoints) {
                if (!isContentfulString(breakpoint)) {
                    continue;
                }
                const columnsNumber = BaseCardListColumnsByBreakpoint[breakpoint];
                const masonryColumnsNumber = Math.floor(columnsNumber / cardWidth);
                const breakpointSize = breakpointsNamesToSizes[breakpoint];
                if (!breakpointSize) {
                    continue;
                }
                masonryBreakpoints[breakpointSize] = masonryColumnsNumber > 0 ? masonryColumnsNumber : 1;
            }
            return masonryBreakpoints;
        };
    }
    componentDidMount() {
        const { firstPage, lazy, limit } = this.props;
        if (typeof this.setContext === 'function') {
            this.setContext();
        }
        if (lazy) {
            this.context?.setInitialPagination({ accumulate: false, limit, page: Number(firstPage) });
            return;
        }
        this.fetchFirstPage();
    }
    componentDidUpdate({ reset: prevReset }) {
        const { reset } = this.props;
        if (reset && prevReset !== reset) {
            this.fetchFirstPage();
        }
    }
    getContext() {
        const { context } = this.state;
        if (!context) {
            throw Error('[BaseCardList]: Context not initialized.');
        }
        return context;
    }
    fetchFirstPage() {
        const { lazy, limit, onNextPage, firstPage } = this.props;
        if (lazy) {
            return;
        }
        if (isFunction(onNextPage)) {
            onNextPage(new PageEvent({ accumulate: false, limit, page: Number(firstPage) }));
        }
    }
    modifyCardSelection(card, remove) {
        const { selectionType = BaseCardList.defaultProps.selectionType } = this.props;
        const { selectedCards } = this.state;
        return this.getContext().modifyCardSelection({
            item: this.getCardKey(card),
            remove,
            selection: selectedCards,
            type: selectionType,
        });
    }
    getCardKey(card) {
        const { keyField } = this.props;
        return String(card[keyField]);
    }
    renderLoadMore() {
        return (React.createElement(LoadMore, { data: this.props.data, done: this.props.done, loadMoreExpression: this.props.loadMoreExpression, loading: this.props.loading, onClick: this.onLoadMoreClick }));
    }
    renderNotFound() {
        return (React.createElement(NotFoundResult, { data: this.props.data, emptyResultSubtitleExpression: this.props.emptyResultSubtitleExpression, emptyResultTitleExpression: this.props.emptyResultTitleExpression, loading: this.props.loading }));
    }
    renderLoading() {
        return (React.createElement(Loading, { loading: this.props.loading }));
    }
    isCardSelectable() {
        const { selectable, selectionType = BaseCardList.defaultProps.selectionType } = this.props;
        return this.getContext().allowCardSelection(selectable, selectionType);
    }
    renderCards(cardsData) {
        const { children, columnGap, masonry, orientation, rowGap, selectionStyle, selectionType, } = this.props;
        const { selectedCards } = this.state;
        if (!children) {
            return null;
        }
        const cardListStyle = {
            '--col-gap': columnGap,
            '--row-gap': rowGap,
        };
        const actualOrientation = isMobileResolution() ? orientation : BaseCardListOrientation.VERTICAL;
        const cards = cardsData.map((card, index) => {
            const cardKeyFieldValue = this.getCardKey(card);
            const cardProps = {
                ...card,
                identifier: `${index}-${cardKeyFieldValue}`,
                onClick: () => this.onItemClick(card),
                onDoubleClick: this.onDoubleClick,
                onMenuClick: this.onContextMenuClick,
                onSelect: (event) => this.updateCardSelection(event, card),
                orientation: actualOrientation,
                role: 'listitem',
                selectable: this.isCardSelectable(),
                selected: this.getContext().isCardSelected(cardKeyFieldValue, selectedCards),
                selectionStyle,
                selectionType,
            };
            return propagateProps(children, cardProps, true);
        });
        const className = classNames({
            'c-deck__cards-wrapper': true,
            [`c-deck__cards-wrapper--${actualOrientation}`]: isEnumValid(BaseCardListOrientation, actualOrientation),
            'o-grid-card-list': true,
        });
        if (masonry) {
            /** Since all cards in a base card list have the same width, this will work */
            const childWidth = Array.isArray(children) ? children[0]?.props?.width : children?.props?.width;
            const cardWidth = Number(childWidth ?? CardProperties.width.defaultValue);
            return (React.createElement(ResponsiveMasonry, { columnsCountBreakPoints: this.getMasonryColumnsCountBreakPoints(cardWidth) },
                React.createElement(Masonry, { gutter: `${rowGap} ${columnGap}` }, cards)));
        }
        return (React.createElement("div", { "aria-label": MessageService.translate('tws.card-list.aria-label'), className: className, role: "list", style: cardListStyle }, cards));
    }
    render() {
        return null;
    }
}
BaseCardList.propTypes = {
    accumulate: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    columnGap: oneOf(enumValues(LayoutGap)).group('layout'),
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultSubtitleExpression: expression,
    emptyResultTitleExpression: expression,
    firstPage: oneOf(enumValues(FirstPage)),
    keyField: string,
    lazy: bool,
    limit: number,
    loading: bool.notEnumerable(),
    loadMoreExpression: expression,
    masonry: bool,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onNextPage: eventListener,
    onSelectItem: eventListener,
    onUnselectItem: eventListener,
    orientation: oneOf(enumValues(BaseCardListOrientation)),
    page: number.notEnumerable(),
    reset: bool.notEnumerable(),
    rowGap: oneOf(enumValues(LayoutGap)).group('layout'),
    selectable: bool,
    selectionStyle: oneOf(enumValues(SelectionStyle)),
    selectionType: oneOf(enumValues(SelectionType)),
    uuid: string,
};
BaseCardList.defaultProps = {
    columnGap: LayoutGap.LARGE,
    done: true,
    firstPage: FirstPage.ZERO,
    keyField: 'id',
    limit: 12,
    loadMoreExpression: 'tws.deck.load-more',
    masonry: false,
    orientation: BaseCardListOrientation.VERTICAL,
    page: 0,
    reset: false,
    rowGap: LayoutGap.LARGE,
    selectable: false,
    selectionStyle: SelectionStyle.NONE,
    selectionType: SelectionType.NONE,
};
BaseCardList.contextType = FetchContext;
export { BaseCardList, };
//# sourceMappingURL=base-card-list.js.map